import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadPdfLogoDataUrl } from "./pdfLogo";

import { formatJalaliDate } from "../shop/utils/dateFormat";
export async function exportSaleReturnsToPDF(
  saleReturns,
  filename = "sale_returns.pdf"
) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let currentY = 10;
  const logoDataUrl = await loadPdfLogoDataUrl();

  doc.setProperties({
    title: "Sale Returns",
    subject: "Sale Returns Data Export",
    creator: "Tawakal Noorzai Trading Company",
  });

  const addHeader = () => {
    const shopName = "شرکت تجارتی توکل نورزی";

    try {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "JPEG", pageWidth - 25, 2, 10, 10);
      }
    } catch (error) {
      console.error("Failed to add logo:", error);
    }

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 102, 204);
    doc.text("Sale Returns Report", pageWidth / 2, 9, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80, 80, 80);
    doc.text(shopName, 10, 7);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${formatJalaliDate()}`, 10, 11);

    return 18;
  };

  currentY = addHeader();

  // Helper function to get first currency value
  const firstCurrencyValue = (obj) => {
    if (obj && typeof obj === "object") {
      const firstEntry = Object.values(obj)[0];
      if (firstEntry && typeof firstEntry === "object")
        return firstEntry.source ?? "-";
      return firstEntry ?? "-";
    }
    return obj ?? "-";
  };

  // Calculate summary totals
  const summaryTotals = saleReturns.reduce((acc, ret) => {
    const totalObj = ret.sale_details?.total_amount || {};
    const currency = Object.keys(totalObj)[0] || "";
    const saleTotal = parseFloat(firstCurrencyValue(ret.sale_details?.total_amount)) || 0;
    const refundTotal = parseFloat(ret.total_refound_amount) || 0;
    
    return {
      totalSaleAmount: acc.totalSaleAmount + saleTotal,
      totalRefundAmount: acc.totalRefundAmount + refundTotal,
      currency: currency || acc.currency
    };
  }, { totalSaleAmount: 0, totalRefundAmount: 0, currency: "" });

  saleReturns.forEach((ret) => {
    if (currentY > pageHeight - 60) {
      doc.addPage();
      currentY = addHeader();
    }

    const totalObj = ret.sale_details?.total_amount || {};
    const currency = Object.keys(totalObj)[0] || "-";
    const saleTotal = totalObj[currency] || "0";

    autoTable(doc, {
      head: [
        [
          "ID",
          "Sale Reference",
          "Return Date",
          "Reason",
          "Sale Total",
          "Refund Total",
          "Item Count",
        ],
      ],
      body: [
        [
          ret.id,
          ret.sale_details?.id ? `Sale #${ret.sale_details.id}` : "-",
          formatJalaliDate(ret.return_date),
          ret.reason || "-",
          `${saleTotal} ${currency}`,
          `${ret.total_refound_amount || "-"} ${currency}`,
          ret.return_items?.length || 0,
        ],
      ],
      startY: currentY,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fontSize: 8,
        fontStyle: "bold",
        fillColor: [51, 102, 204],
        textColor: [255, 255, 255],
        halign: "center",
        cellPadding: 2,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 250],
      },
      margin: { top: 1, bottom: 1, left: 3, right: 3 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) currentY = addHeader();
      },
    });

    currentY = doc.lastAutoTable.finalY + 0;

    if (ret.return_items?.length) {
      autoTable(doc, {
        head: [["Product", "Return Qty", "Return Price", "Refund Amount"]],
        body: ret.return_items.map((item) => {
          const priceObj = item.sale_item_details?.sale_price || {};
          const salePrice = priceObj || "0";
          return [
            item.sale_item_details?.product?.name || "-",
            item.return_quantity,
            salePrice,
            item.refund_amount,
          ];
        }),
        startY: currentY,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          halign: "center",
          valign: "middle",
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
          fillColor: [220, 230, 242],
          textColor: [50, 50, 50],
          halign: "center",
          cellPadding: 2,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        margin: { top: 1, bottom: 1, left: 3, right: 3 },
      });

      currentY = doc.lastAutoTable.finalY + 2;
    }
  });

  // Add summary table
  if (currentY > pageHeight - 40) {
    doc.addPage();
    currentY = addHeader();
  }

  autoTable(doc, {
    head: [["Summary", "Total Sale Amount", "Total Refund Amount"]],
    body: [[
      "TOTALS",
      `${summaryTotals.totalSaleAmount.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalRefundAmount.toFixed(2)} ${summaryTotals.currency}`
    ]],
    startY: currentY + 5,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      halign: "center",
      fontStyle: "bold",
    },
    headStyles: {
      fontSize: 10,
      fontStyle: "bold",
      fillColor: [51, 102, 204],
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fillColor: [245, 245, 245],
      fontStyle: "bold",
    },
    margin: { left: 3, right: 3 },
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(0, pageHeight - 11, pageWidth, pageHeight - 11);

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("تجزوی هول سیل", pageWidth / 2, pageHeight - 5, {
      align: "center",
    });

    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, pageHeight - 5, {
      align: "right",
    });
  }

  doc.save(filename);
}

